import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, ArrowRight, ArrowLeft } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Field, GradButton } from "@/components/erp";
import { useAuth, applyTheme } from "@/context/AuthContext";
import api, { errMsg } from "@/lib/api";

const TEMA_ADLARI = { gece: "Gece (Navy)", grafit: "Grafit (Turuncu)", okyanus: "Okyanus (Turkuaz)" };
const TEMA_RENK = { gece: ["#3b82f6", "#10b981", "#8b5cf6"], grafit: ["#f97316", "#eab308", "#ef4444"], okyanus: ["#06b6d4", "#14b8a6", "#6366f1"] };

const SektorAdim = ({ sablonlar, secili, onSec }) => (
  <div className="grid md:grid-cols-3 gap-4">
    {sablonlar.map((s) => (
      <button key={s.kod} data-testid={`sektor-${s.kod}`} onClick={() => onSec(s.kod)}
        className={`text-left flex flex-col justify-start glass rounded-2xl p-6 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 ${secili === s.kod ? "grad-border glow" : ""}`}>
        <div className="flex justify-between items-start">
          <h3 className="font-head text-lg font-semibold">{s.ad}</h3>
          {secili === s.kod && <span className="w-6 h-6 rounded-full grad-bg flex items-center justify-center"><Check className="w-3.5 h-3.5 text-white" /></span>}
        </div>
        <p className="text-xs text-muted-foreground mt-2">{s.aciklama}</p>
        <div className="mt-5 text-[10px] uppercase tracking-widest text-muted-foreground">İstasyon zinciri</div>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {s.istasyonlar.map((i, n) => <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10">{n + 1}. {i}</span>)}
        </div>
        <div className="mt-4 text-[10px] uppercase tracking-widest text-muted-foreground">Ürün alanları</div>
        <div className="text-xs mt-1.5 text-foreground/80">{s.oznitelikler.join(" · ")}</div>
        {s.lot_skt_zorunlu && <div className="mt-3 text-[11px] text-amber-400">Lot / SKT takibi zorunlu</div>}
      </button>
    ))}
  </div>
);

const FirmaAdim = ({ form, set }) => (
  <div className="glass rounded-2xl p-6 grid sm:grid-cols-2 gap-4 max-w-3xl">
    {[["unvan", "Ticari Unvan *"], ["kisa_ad", "Kısa Ad (menüde görünür)"], ["vergi_dairesi", "Vergi Dairesi"], ["vergi_no", "Vergi No"],
      ["telefon", "Telefon"], ["email", "E-posta"]].map(([k, l]) => (
      <Field key={k} label={l}><Input data-testid={`kurulum-${k}`} value={form[k]} onChange={(e) => set(k, e.target.value)} className="bg-white/5" /></Field>
    ))}
    <Field label="Adres" className="sm:col-span-2"><Input data-testid="kurulum-adres" value={form.adres} onChange={(e) => set("adres", e.target.value)} className="bg-white/5" /></Field>
  </div>
);

const TemaAdim = ({ temalar, secili, onSec }) => (
  <div className="grid sm:grid-cols-3 gap-4 max-w-3xl">
    {temalar.map((t) => (
      <button key={t} data-testid={`tema-${t}`} onClick={() => onSec(t)}
        className={`glass rounded-2xl p-5 text-left transition-transform hover:-translate-y-1 ${secili === t ? "grad-border glow" : ""}`}>
        <div className="h-16 rounded-xl" style={{ background: `linear-gradient(120deg, ${TEMA_RENK[t].join(",")})` }} />
        <div className="mt-3 font-medium text-sm">{TEMA_ADLARI[t]}</div>
      </button>
    ))}
  </div>
);

const ADIMLAR = ["Sektör şablonu", "Firma bilgileri", "Tema"];

export default function Kurulum() {
  const { kurulum, user, loadKurulum, loadAyar } = useAuth();
  const nav = useNavigate();
  const [adim, setAdim] = useState(0);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ sektor: "", unvan: "", kisa_ad: "", vergi_dairesi: "", vergi_no: "", telefon: "", email: "", adres: "", tema: "gece" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  if (user?.role !== "admin") {
    return <div className="min-h-screen app-bg flex items-center justify-center p-6 text-center text-muted-foreground" data-testid="kurulum-bekleniyor">Sistem kurulumu henüz yapılmadı. Lütfen yöneticinizle iletişime geçin.</div>;
  }

  const ileri = () => {
    if (adim === 0 && !form.sektor) return toast.error("Bir sektör şablonu seçin");
    if (adim === 1 && !form.unvan.trim()) return toast.error("Ticari unvan zorunlu");
    setAdim(adim + 1);
  };

  const kur = async () => {
    setBusy(true);
    try {
      await api.post("/kurulum", form);
      await loadKurulum();
      await loadAyar();
      toast.success("Kurulum tamamlandı");
      nav("/");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen app-bg p-6 sm:p-12">
      <div className="max-w-6xl mx-auto">
        <div className="text-[11px] tracking-[0.3em] uppercase text-muted-foreground">İlk kurulum</div>
        <h1 className="font-head text-4xl sm:text-5xl font-bold mt-2">Bu kurulum <span className="grad-text">hangi işletme</span> için?</h1>
        <p className="text-muted-foreground mt-3 max-w-2xl text-sm">Seçtiğiniz sektör; ürün alanlarını, istasyon zincirini, birimleri ve ambalaj kurallarını otomatik yükler. Hepsi sonradan Ayarlar'dan değiştirilebilir.</p>
        <div className="flex gap-3 my-10 flex-wrap">
          {ADIMLAR.map((a, i) => (
            <div key={a} className={`flex items-center gap-2 text-sm ${i <= adim ? "text-white" : "text-muted-foreground"}`}>
              <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${i <= adim ? "grad-bg" : "bg-white/5"}`}>{i + 1}</span>{a}
              {i < ADIMLAR.length - 1 && <span className="w-10 h-px bg-white/10 ml-1" />}
            </div>
          ))}
        </div>
        {adim === 0 && <SektorAdim sablonlar={kurulum?.sablonlar || []} secili={form.sektor} onSec={(v) => set("sektor", v)} />}
        {adim === 1 && <FirmaAdim form={form} set={set} />}
        {adim === 2 && <TemaAdim temalar={kurulum?.temalar || []} secili={form.tema} onSec={(v) => { set("tema", v); applyTheme(v); }} />}
        <div className="flex gap-3 mt-10">
          {adim > 0 && <button data-testid="kurulum-geri" onClick={() => setAdim(adim - 1)} className="px-5 py-2.5 rounded-full border border-white/10 text-sm inline-flex items-center gap-2 hover:bg-white/5"><ArrowLeft className="w-4 h-4" />Geri</button>}
          {adim < 2 ? <GradButton data-testid="kurulum-ileri" onClick={ileri}>Devam <ArrowRight className="w-4 h-4" /></GradButton>
            : <GradButton data-testid="kurulum-tamamla" disabled={busy} onClick={kur}>{busy ? "Kuruluyor…" : "Kurulumu tamamla"} <Check className="w-4 h-4" /></GradButton>}
        </div>
      </div>
    </div>
  );
}
